// File: app/src/utils/pointerEventsGuard.js
//
// Pure DOM helpers behind the app-wide "stuck pointer-events" guard (#72,
// recurrence of #52).
//
// Radix's DismissableLayer sets `document.body.style.pointerEvents = "none"`
// while a modal layer is open and restores it from a cleanup effect owned by
// that layer. If the layer is unmounted by the same commit that closes it —
// e.g. a destructive action fired from a DropdownMenuItem → AlertDialog whose
// handler then replaces the data the whole subtree renders from — that cleanup
// never runs and the lock stays welded to <body>. The page still scrolls, but
// nothing is clickable, until a refresh.
//
// These helpers answer one question: is a body-level pointer-events lock still
// backed by a layer that is genuinely open? If not, it is safe to release.
// No React imports; the only mutation is clearing the inline style we own.

// Selectors matching a Radix layer that is actually open right now. Popper
// content (dropdowns, selects, popovers) is portalled into a wrapper element;
// dialogs, alert dialogs and menus carry `data-state` on the content node.
const OPEN_OVERLAY_SELECTORS = [
  "[data-radix-popper-content-wrapper]",
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
].join(",");

// Is any modal/popper layer open in this document?
export const hasOpenOverlay = (doc = document) => {
  if (!doc || typeof doc.querySelector !== "function") return false;
  return !!doc.querySelector(OPEN_OVERLAY_SELECTORS);
};

// Is <body> currently carrying the inline pointer-events lock?
export const isPointerEventsLocked = (doc = document) => {
  const body = doc?.body;
  if (!body || !body.style) return false;
  return body.style.pointerEvents === "none";
};

// A lock is stuck when the body is locked but nothing is open to justify it.
export const isPointerEventsStuck = (doc = document) =>
  isPointerEventsLocked(doc) && !hasOpenOverlay(doc);

// Release a stuck lock. Returns true if it actually cleared something, so
// callers can log or test the recovery. A lock with an open layer behind it is
// left alone — that one is doing its job.
export const releaseStuckPointerEvents = (doc = document) => {
  if (!isPointerEventsStuck(doc)) return false;
  doc.body.style.pointerEvents = "";
  return true;
};
